import { memo, useEffect, useRef, forwardRef, ForwardedRef } from "react";
import { noticeError } from "@/utils/newrelic/requests";
import styles from "./AddToBasketESIWrapper.module.scss";
import cx from "classnames";

const BASKET_CLIENT_ID = "web_pdp_scaffold_ui";

type Props = {
  availableToOrder: boolean;
  expressCheckoutEnabled: boolean;
  hideAddToBasket: boolean;
  pimProductId: string;
  price: string;
  recsPlacementId: string;
  sendVariantIdToBasket: boolean;
  shipFromStore: boolean;
  shouldEnableAddToBasket: boolean;
  showAddToBasketButton: boolean;
  skuId: string;
  title: string;
  variantId: string;
  stockApiBaseUrl: string;
  stockApiKey: string;
  showWishlistIconButtonVariant: string;
};

export const AddToBasketESIWrapperUI = forwardRef(
  (
    {
      pimProductId,
      skuId,
      variantId,
      title,
      price,
      availableToOrder,
      hideAddToBasket,
      sendVariantIdToBasket,
      recsPlacementId,
      shouldEnableAddToBasket,
      expressCheckoutEnabled,
      showAddToBasketButton,
      shipFromStore,
      showWishlistIconButtonVariant,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const fixedSrc = useRef(
      getAddToBasketEsiSrcUrl(
        pimProductId,
        sendVariantIdToBasket,
        {
          skuId,
          variantId,
          title,
          price,
          availableToOrder,
        },
        shouldEnableAddToBasket,
        hideAddToBasket,
        recsPlacementId,
        expressCheckoutEnabled,
        showAddToBasketButton,
        shipFromStore,
        showWishlistIconButtonVariant,
      ),
    );

    return <MemoedEsi srcUrl={fixedSrc.current} ref={ref} />;
  },
);
AddToBasketESIWrapperUI.displayName = "AddToBasketESIWrapperUI";

const MemoedEsi = memo(
  forwardRef(
    ({ srcUrl }: { srcUrl: string }, ref: ForwardedRef<HTMLDivElement>) => {
      const isMounting = useRef(true);

      useEffect(() => {
        if (!isMounting.current) {
          noticeError("Add to Basket component re-rendered unexpectedly!");
        }
        isMounting.current = false;
      });

      useEffect(() => {
        if (
          document
            .querySelector("#web-pdp-scaffold-ui-add-to-basket")
            ?.nodeName?.toLowerCase() === "esi:include" &&
          window.location.origin.includes("johnlewis.com")
        ) {
          noticeError(
            `esi:include tag rendered on the client "web-pdp-scaffold-ui-add-to-basket"`,
          );
        }
      }, []);

      return (
        <div
          id="product:basket:a2besi"
          data-testid="product:basket:a2besi"
          className={cx("esi-wrapper", styles.wrapper)}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            // nosemgrep
            __html: `<esi:include id="web-pdp-scaffold-ui-add-to-basket" ${
              // nosemgrep
              srcUrl ? ` src="${srcUrl}"` : ""
            } />`,
          }}
          ref={ref}
        ></div>
      );
    },
  ),
);
MemoedEsi.displayName = "MemoedEsi";

function getAddToBasketEsiSrcUrl(
  pimProductId: string,
  sendVariantId: boolean,
  variant: {
    skuId: string;
    variantId: string;
    title: string;
    price: string;
    availableToOrder: boolean;
  },
  shouldEnableAddToBasket: boolean,
  hideAddToBasket: boolean,
  recsPlacementId: string,
  expressCheckoutEnabled: boolean,
  showAddToBasketButton: boolean,
  shipFromStore: boolean,
  showWishlistIconButtonVariant: string,
) {
  const queryString = makeQueryString({
    skuId: variant.skuId,
    variantId: sendVariantId ? variant.variantId : "",
    title: variant.title,
    price: variant.price,
    enabled: String(shouldEnableAddToBasket),
    includeCoreAssets: "false",
    hidden: String(hideAddToBasket),
    clientId: BASKET_CLIENT_ID,
    recsPlacementId,
    enableExpress: String(expressCheckoutEnabled),
    showAddToBasket: String(showAddToBasketButton),
    isShipFromStore: String(shipFromStore),
    showWishlistIconButtonVariant: String(showWishlistIconButtonVariant),
    isOutOfStock: String(!variant.availableToOrder),
  });

  return `/esi/add-to-basket/${pimProductId}?${queryString}`;
}

function makeQueryString(params: Record<string, string>) {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
}
